import React, { useState, useEffect } from 'react';

import './CalculationTool.scss';
import icons from '../../shared/assets/icons';
import { getString } from '../../helpers/styles';
import Transition from '../Transition/Transition.jsx';
import CalendarPopup from '../CalendarPopup/CalendarPopup.jsx';
import { setCustomValidity, resetValidity } from '../../helpers/form';
import { format as formatDate, buildDateString } from '../../helpers/date';

const { Calendar, Time } = icons;

export default function CalculationTool(props) {
  const {
    type,
    locale,
    username,
    loginUrl,
    onboarding,
    changeUserData,
    nextOnboardingStep,
    showDueDateWarning
  } = props;

  const dueDateTool = type === 'dueDate';
  const fertilityTool = type === 'fertility';

  const passedDate =
    props.initialDate && props.initialDate.split('-').reverse().join('/');

  const initialDate = passedDate ? new Date(passedDate) : new Date();

  if (!passedDate && !fertilityTool)
    initialDate.setDate(initialDate.getDate() - 7);

  const [foldedOut, togglePopup] = useState(false);

  const [selectedDate, changeSelectedDate] = useState({
    day: initialDate.getDate(),
    year: initialDate.getFullYear(),
    month: initialDate.getMonth() + 1
  });

  const [selectedLength, changeSelectedLength] = useState(28);
  const [calculatedResult, showCalculatedResult] = useState(null);

  const [validationError, setValidationError] = useState(null);

  const [iOSdatepicker, useIOSdatepicker] = useState(false);

  const minDateForIOSDatePicker = new Date();

  minDateForIOSDatePicker.setDate(minDateForIOSDatePicker.getDate() - 40 * 7);

  const inputValue =
    selectedDate.month &&
    `${selectedDate.day < 10 ? `0${selectedDate.day}` : selectedDate.day}-${
      selectedDate.month < 10 ? `0${selectedDate.month}` : selectedDate.month
    }-${selectedDate.year}`;

  useEffect(() => {
    const date = document.getElementById('calculationTool_first_day');
    const iOSDate = document.querySelector('.calculationTool_ios_date_input');

    setCustomValidity(
      date,
      `Oei, deze datum klopt niet helemaal.
      Probeer het nog een keer`
    );

    setCustomValidity(
      iOSDate,
      `Oeps dat ging even mis.
      Je laatste dag van je menstruatie ligt in het verleden`
    );

    resetValidity(iOSDate, 'valueMissing');
  });

  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.platform);

    if (iOS) useIOSdatepicker(true);

    document.body.addEventListener('click', closePopup);

    return () => document.body.removeEventListener('click', closePopup);
  }, []);

  return (
    <section
      id="calculationTool"
      {...(onboarding && { className: 'calculationTool_onboarding' })}
    >
      {calculatedResult ? (
        <ResultSection
          type={type}
          locale={locale}
          loginUrl={loginUrl}
          username={username}
          length={selectedLength}
          onboarding={onboarding}
          dueDateTool={dueDateTool}
          fertilityTool={fertilityTool}
          changeUserData={changeUserData}
          calculatedResult={calculatedResult}
          showDueDateWarning={showDueDateWarning}
          nextOnboardingStep={nextOnboardingStep}
          showCalculatedResult={showCalculatedResult}
        />
      ) : (
        <form id="calculationTool_form" onSubmit={(e) => calculate(e)}>
          <div className="calculationTool_form_line">
            <label htmlFor="calculationTool_first_day">
              Eerste dag van je laatste menstruatie
            </label>

            {iOSdatepicker ? (
              <input
                required
                type="date"
                name="firstDay"
                placeholder="DD/MM/YY"
                defaultValue={inputValue}
                id="calculationTool_first_day"
                max={buildDateString(new Date())}
                className="calculationTool_ios_date_input"
                min={buildDateString(minDateForIOSDatePicker)}
                onChange={(e) => {
                  if (e.target.value) e.target.placeholder = '';
                }}
              />
            ) : (
              <React.Fragment>
                <input
                  required
                  name="firstDay"
                  autoComplete="off"
                  placeholder="Datum"
                  data-validated-value=""
                  defaultValue={inputValue}
                  id="calculationTool_first_day"
                  onChange={(e) => formatDate(e.target)}
                  onClick={(e) => (e.target.placeholder = 'DD-MM-JJJJ')}
                  pattern="(0[1-9]|1[0-9]|2[0-9]|3[01])-(0[1-9]|1[012])-[0-9]{4}"
                />

                <span
                  id="calculationTool_calendar"
                  className={getString(
                    'calculationTool_calendar_scaled',
                    foldedOut
                  )}
                  onClick={() => togglePopup(!foldedOut)}
                >
                  <Calendar />
                </span>
              </React.Fragment>
            )}
          </div>

          <div className="calculationTool_form_line">
            <label htmlFor="calculationTool_cycle_length">
              Lengte van je cyclus in dagen
            </label>

            <input
              required
              name="length"
              type="number"
              min="20"
              max="45"
              autoComplete="off"
              value={selectedLength}
              id="calculationTool_cycle_length"
              onChange={(e) => changeSelectedLength(e.target.value)}
            />

            <span>
              <Time />
            </span>
          </div>

          {validationError && (
            <span id="calculationTool_error">{validationError}</span>
          )}

          <button
            onClick={() => {
              if (props.actionOnCalculating) props.actionOnCalculating();
            }}
            className="calculationTool_button"
          >
            Bereken
          </button>

          <Transition on={foldedOut}>
            <CalendarPopup
              withTriangle={true}
              year={selectedDate.year}
              togglePopup={togglePopup}
              month={selectedDate.month}
              field="calculationTool_first_day"
              isLimitedByPastPregnancyPeriod={true}
              changeSelectedDate={changeSelectedDate}
            />
          </Transition>
        </form>
      )}
    </section>
  );

  function calculate(e) {
    e.preventDefault();

    const length = e.target.length.value;

    const [day, month, year] = iOSdatepicker
      ? e.target.firstDay.value.split('-').reverse()
      : e.target.firstDay.value.split('-');

    const date = new Date(year, month - 1, day);

    if (dueDateTool) {
      setValidationError(null);

      const deadLineDate = new Date();

      deadLineDate.setDate(deadLineDate.getDate() - 7);

      const differenceInTime = deadLineDate.getTime() - date.getTime();

      if (differenceInTime < 0)
        return setValidationError('Geef een geldige datum op');

      const differenceInWeeks = Math.ceil(
        differenceInTime / (1000 * 3600 * 24 * 7)
      );

      // Naegele's Rule
      const delta = 280 - 28 + +length;

      date.setDate(date.getDate() + delta);

      return showCalculatedResult({ date, week: differenceInWeeks });
    }

    if (fertilityTool) {
      const [fruitfulPeriod, ovulationDate, menstruationPeriod] =
        calculateOvulationIndicators(new Date(date), length);

      return showCalculatedResult({
        fruitfulPeriod,
        ovulationDate,
        menstruationPeriod
      });
    }
  }

  function closePopup(e) {
    if (e.target.closest('#calendarPopup')) return;

    togglePopup(false);
  }
}

function ResultSection(props) {
  const {
    locale,
    loginUrl,
    username,
    onboarding,
    dueDateTool,
    fertilityTool,
    changeUserData,
    calculatedResult,
    showDueDateWarning,
    nextOnboardingStep,
    showCalculatedResult
  } = props;

  const [savedResult, setSavedResult] = useState(false);

  if (dueDateTool)
    return (
      <div id="calculationTool_due_date_result">
        <p>Jouw uitgerekende datum is:</p>

        <span id="calculationTool_calculated_date">
          {calculatedResult.date.toLocaleString(locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
        </span>

        <span
          id="calculationTool_recalculate_label"
          onClick={() => {
            showCalculatedResult(null);

            if (showDueDateWarning) showDueDateWarning(false);
          }}
        >
          Bereken opnieuw
        </span>

        <p id="calculationTool_calculated_week">
          Je bent nu in <span>week {calculatedResult.week}</span>
        </p>

        <button
          className="calculationTool_button"
          onClick={() =>
            onboarding
              ? nextOnboardingStep(calculatedResult)
              : !savedResult && saveDueDate()
          }
        >
          {onboarding ? 'Volgende' : savedResult ? 'Opgeslagen' : 'Opslaan'}
        </button>
      </div>
    );

  if (fertilityTool)
    return (
      <div id="calculationTool_fertility_result">
        <h3>Jouw verwachte ovulatie datum en vruchtbare dagen</h3>

        <CalendarPopup
          onDateClick={() => {}}
          disableSwitching={true}
          markedDays={calculatedResult}
          year={calculatedResult.ovulationDate.getFullYear()}
          month={calculatedResult.ovulationDate.getMonth() + 1}
        />

        <ul id="calculationTool_legend">
          <li>
            <span id="calculationTool_legend_menstruation_period" />
            <div>Ongesteld</div>
          </li>
          <li>
            <span id="calculationTool_legend_ovulation_date" />
            <div>Ovulatie</div>
          </li>
          <li>
            <span id="calculationTool_legend_fruitful_period" />
            <div>Vruchtbare periode</div>
          </li>
        </ul>

        <p>
          Let op: We kunnen je ovulatie niet exact bepalen, maar geven een
          schatting op basis van gemiddelden. Lees hieronder hoe dat zit.
        </p>

        <h3>Toekomstige ovulatie data en vruchtbare dagen</h3>

        <div id="calculationTool_future_periods">
          <div>
            <p>Ovulatie</p>
            <p>Vruchtbare dagen</p>
          </div>

          <ul>
            {Array.from({ length: 6 }).map((_, idx) => {
              const monthIndexNumber = idx + 1;
              const firstDay = calculatedResult.menstruationPeriod.start;

              const date = new Date(
                firstDay.getFullYear(),
                firstDay.getMonth(),
                firstDay.getDate() + props.length * monthIndexNumber
              );

              const [fruitfulPeriod, ovulationDate] =
                calculateOvulationIndicators(date, props.length);

              return (
                <li key={idx}>
                  <p>{format(ovulationDate)}</p>

                  <p>
                    {format(fruitfulPeriod.start)} -{' '}
                    {format(fruitfulPeriod.end)}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>

        <span id="calculationTool_recalculate_label" onClick={() => reset()}>
          Opnieuw berekenen
        </span>
      </div>
    );

  function reset() {
    showCalculatedResult(null);

    window.scrollTo(0, 0);
  }

  function saveDueDate() {
    if (!username) return (location.href = loginUrl);

    const date = new Date(
      calculatedResult.date.getTime() -
        calculatedResult.date.getTimezoneOffset() * 60000
    ).toISOString();

    changeUserData({ variables: { username, edd: date.split('T')[0] } });

    setSavedResult(true);
  }
}

function calculateOvulationIndicators(date, length) {
  const menstruationPeriodStart = date;

  const menstruationPeriodEnd = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 6
  );

  const ovulationDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + Math.ceil(length / 2) - 1
  );

  const fruitfulPeriodStart = new Date(
    ovulationDate.getFullYear(),
    ovulationDate.getMonth(),
    ovulationDate.getDate() - 3
  );

  const fruitfulPeriodEnd = new Date(
    ovulationDate.getFullYear(),
    ovulationDate.getMonth(),
    ovulationDate.getDate() + 1
  );

  const menstruationPeriod = {
    start: menstruationPeriodStart,
    end: menstruationPeriodEnd
  };

  const fruitfulPeriod = {
    start: fruitfulPeriodStart,
    end: fruitfulPeriodEnd
  };

  return [fruitfulPeriod, ovulationDate, menstruationPeriod];
}

function format(date) {
  return date.toLocaleString('en-US', { day: 'numeric', month: 'long' });
}
